import React, { useState } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import {
  faUserCircle,
  faInfoCircle,
  faMicrophoneAlt,
  faCalendarAlt,
  faBirthdayCake,
  faStethoscope,
  faChartBar,
} from '@fortawesome/free-solid-svg-icons';
import CriticalAlerts from "./criticalalerts";
import FocusCentral from "./focusCentral";
import ClinicalIntelligence from "./clinicalintelligence";
import RiskRadar from "./riskradar";
import SmartQuestions from "./smartquestions";
import ExamBox from "./exambox";
import dayjs from "dayjs";
import CoreGap from "./coregap";
import ShowTimeLine from "./timeline";
import useModalStore from "zustand/zustandstore";

const PatientData = ({ data,fileName,patientid,setui,recentRecord}) => {
  const [view,setView]=useState(0);
    const { darkmode } = useModalStore();
  if (!data) return null;

  const { patient_info, patient_insights } = data;

const parseDemographics = (demographics) => {
  const result = {
    DOB: null,
    Age: null,
    VisitType: null,
  };

  if (!demographics) return result;

  // Extract DOB
  const dobMatch = demographics.match(/DOB[:\s]*([0-9]{4}-[0-9]{2}-[0-9]{2})/i);
  const dob = dobMatch?.[1] ? dayjs(dobMatch[1]) : null;

  // Extract Visit Date from multiple formats
  let visitDateStr = null;

  // Format: at 2024-10-25 Visit
  const visitMatch1 = demographics.match(/at\s+([0-9]{4}-[0-9]{2}-[0-9]{2})\s+Visit/i);
  if (visitMatch1) {
    visitDateStr = visitMatch1[1];
  }

  // Format: (at current 2025 visit)
  const visitMatch2 = demographics.match(/\(.*?(current\s+[0-9]{4}).*?visit.*?\)/i);
  if (visitMatch2) {
    const year = visitMatch2[1].match(/\d{4}/)?.[0];
    if (year) {
      // Use July 1st of the year as default visit date
      visitDateStr = `${year}-07-01`;
    }
  }

  // Format: "as of 2025-07-07"
  const visitMatch3 = demographics.match(/as of\s+([0-9]{4}-[0-9]{2}-[0-9]{2})/i);
  if (visitMatch3) {
    visitDateStr = visitMatch3[1];
  }

  const visitDate = visitDateStr ? dayjs(visitDateStr) : dayjs(); // fallback to today if no date

  // Calculate Age
  let age = null;
  if (dob && dob.isValid() && visitDate.isValid()) {
    age = visitDate.diff(dob, 'year');
  }

  // Final values
  result.DOB = dob?.isValid() ? dob.format('DD/MM/YYYY') : null;
  result.Age = age?.toString() || null;

  // Optional: Extract VisitType if available
  const visitTypeMatch = demographics.match(/Visit Type[:\s]*([a-zA-Z]+)/i);
  if (visitTypeMatch) {
    result.VisitType = visitTypeMatch[1].trim();
  } else if (visitMatch1) {
    result.VisitType = `${visitMatch1[1]} Visit`;
  } else if (visitMatch2) {
    result.VisitType = visitMatch2[1];
  }

  return result;
};



const alerts = patient_insights?.critical_alerts?.data_rows || [];
const followups = patient_insights?.followup_central?.items || [];
const clinicalIntel = patient_insights?.clinical_intel?.data_rows || [];
const smartQs = patient_insights?.smart_questions?.questions || [];
const riskRadar = patient_insights?.risk_radar?.risk_factors || [];
const careGaps = patient_insights?.risk_radar?.care_gaps || [];


// Example usage
const { DOB, Age ,VisitType} = parseDemographics(patient_info.demographics);



  return (
    <div className="w-full px-2 md:px-4 ">


<div className="bg-white rounded-[12.75px] shadow-[0_0_4px_0_rgba(0,0,0,0.25)] backdrop-blur-[3px] p-4 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm">

  {/* Left: Avatar + Info */}
  <div className="flex items-center gap-4 mb-3 sm:mb-0">
    {/* <button className="bg-gray-700 text-white text-xs font-medium rounded-md px-3 py-2">Switch Patient</button> */}

    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 font-semibold text-sm">
      {patient_info.initials }
    </div>

    <div className="leading-tight space-y-0.5 text-start">
      <div className="text-sm font-semibold text-gray-900">{patient_info.name}</div>
      <div className="flex flex-wrap gap-3 text-xs text-gray-600">
        <span>ID: {patientid }</span>
        <span>DOB: {DOB }</span>
        <span>Age: {Age}</span>
        {/* <span>🕒 {patient_info.time}</span> */}
        <span>{VisitType }</span>
      </div>
    </div>
  </div>

  {/* Right: Status and Button */}
{recentRecord && Object.keys(recentRecord).length > 0 && (
  <div className="flex items-center gap-4">
    <div
      className={`flex items-center text-sm font-medium gap-1 ${
        recentRecord.transcriptionstatus === "Processing"
          ? "text-gray-400"
          : "text-red-600"
      }`}
    >
      <svg
        className={`w-4 h-4 ${
          recentRecord.transcriptionstatus === "Processing"
            ? "text-gray-400"
            : "text-red-600"
        }`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M8.257 3.099c.765-1.36 2.683-1.36 3.449 0l6.516 11.591c.75 1.335-.213 2.99-1.724 2.99H3.465c-1.511 0-2.474-1.655-1.724-2.99L8.257 3.1zM9 13h2v2H9v-2zm0-6h2v5H9V7z"
          clipRule="evenodd"
        />
      </svg>
      Ready for Review
    </div>

    <button
      onClick={() => setui(1)}
      disabled={recentRecord.transcriptionstatus === "Processing"}
      className={`font-medium text-xs px-4 py-1.5 rounded-md ${
        recentRecord.transcriptionstatus === "Processing"
          ? "bg-gray-200 text-gray-500 cursor-not-allowed"
          : "bg-blue-100 text-blue-700 hover:bg-blue-200"
      }`}
    >
      Preview Note
    </button>
  </div>
)}


</div>




<div className="flex gap-4">
  {/* Left: Main Content Area */}
  <div className="flex-1 flex flex-col gap-4">
    
    {/* Row 1: Critical Alerts + Focus Central */}
    {(alerts.length > 0 || followups.length > 0) && (
      <div className="flex gap-4">
        {alerts.length > 0 && (
          <div className="w-1/2">
            <CriticalAlerts view={view} patient_insights={patient_insights} />
          </div>
        )}
        {followups.length > 0 && (
          <div className={alerts.length > 0 ? "w-1/2" : "w-full"}>
            <FocusCentral view={view} patient_insights={patient_insights} />
          </div>
        )}
      </div>
    )}

    {/* Row 2: Clinical Intelligence + Smart Questions */}
    {(clinicalIntel.length > 0 || smartQs.length > 0) && (
      <div className="flex flex-col md:flex-row gap-4">
        {clinicalIntel.length > 0 && (
          <div className={smartQs.length > 0 ? "md:w-[70%] w-full" : "w-full"}>
            <ClinicalIntelligence view={view} data={patient_insights} />
          </div>
        )}
        {smartQs.length > 0 && (
          <div className={clinicalIntel.length > 0 ? "md:w-[30%] w-full" : "w-full"}>
            <SmartQuestions view={view} data={patient_insights} />
          </div>
        )}
      </div>
    )}

    {/* Row 3: Risk Radar + Core Gap */}
    {(riskRadar.length > 0 || careGaps.length > 0) && (
      <div className="flex gap-4">
        {riskRadar.length > 0 && (
          <div className="w-1/2">
            <RiskRadar view={view} data={patient_insights} />
          </div>
        )}
        {careGaps.length > 0 && (
          <div className="w-1/2">
            <CoreGap view={view} data={patient_insights} />
          </div>
        )}
      </div>
    )}
  </div>

  {/* Right: Care Central (Always visible) */}
  <div className="w-[280px] flex-shrink-0">
    <ShowTimeLine patientid={patientid} usertype={'PRO'} />
  </div>
</div>



    </div>
  );
};

export default PatientData;
